import { useCallback, useEffect } from 'react';
import { useDebounceCallback } from 'usehooks-ts';
import { confirmationModal } from 'vl-common/src/components/Modals';
import { useSessionAppointment } from '@hooks/useSessionAppointment';
import { useSessionFormState } from '@hooks/useSessionFormState';
import { useProtectionData, ProtectionStatus } from '@hooks/useProtectionData';
import useAppointmentPrescriptions from '@hooks/useAppointmentPrescriptions';
import { Form } from 'antd';
import { SessionForm } from '@components/Clinicians/session-form';
import useCallStatus, { CallStatus, VideoCallStatusMap } from '@hooks/useCallStatus';
import useAppointmentStatus from '@hooks/useAppointmentStatus';

const useDPACheck = () => {
  const { appointment } = useSessionAppointment();
  const { protectionData } = useProtectionData();
  const { case_review: isCaseReview } = appointment || { case_review: false };
  const [, setAlert] = useSessionFormState('alert', null);
  const dpaCheckKey = `showDPACheck[${appointment?.appointment_id}]`;
  const form = Form.useFormInstance<SessionForm>();
  // used to ensure subType is definitely set
  const formSubType = form.getFieldValue('action_subtype_REFF2F');
  const [isDPACheck, setIsDPACheck] = useSessionFormState('isDPACheck', false);
  const isDPACheckCR = isCaseReview && isDPACheck;

  const appointmentStatus = useAppointmentStatus(appointment);
  const callStatus = useCallStatus(appointment);

  const isVideoCallInProgressOrComplete = (
    [VideoCallStatusMap.InProgress, VideoCallStatusMap.Completed] as CallStatus[]
  ).includes(callStatus);
  const hasCallEndEvent = !!appointmentStatus?.base?.clin_end;
  const hasVideoCallOccurred = isVideoCallInProgressOrComplete || hasCallEndEvent;

  const { prescribeMedication } = useAppointmentPrescriptions();
  const [subType] = useSessionFormState('action_subtype_REFF2F');

  const isCancerCheckSelected =
    !!formSubType && !!form.getFieldsValue().action_subtype_REFF2F && subType === 'CHK4CANC';
  const hasClinicianInitiatedCall = !!sessionStorage.getItem(dpaCheckKey);

  // DPA check becomes required when:
  // - medication is prescribed
  // - "Check for Cancer" action is selected
  // - clinician called patient at least once (audio/video)

  const hasCompletedDPACheck = protectionData?.dpa_check_by !== null;
  const showDPACheck =
    !!prescribeMedication ||
    isCancerCheckSelected ||
    hasVideoCallOccurred ||
    hasClinicianInitiatedCall ||
    hasCompletedDPACheck;
  const dpaCheckResult = protectionData?.dpa_check_state;
  const dpaPassedStatuses = [ProtectionStatus.NoResponse, ProtectionStatus.Recognised, ProtectionStatus.Succeeded];
  const dpaCheckHasPassed = hasCompletedDPACheck && dpaPassedStatuses.includes(dpaCheckResult as number); // Failed DPA returns dpa_check_state === '-1'

  const debouncedSetIsDPACheckRequired = useDebounceCallback(setIsDPACheck, 100);

  useEffect(() => {
    debouncedSetIsDPACheckRequired(showDPACheck);
  }, [showDPACheck]);

  const validateDPACheck = async () => {
    // If DPA check has not passed, then show alert
    if (isDPACheck && !dpaCheckHasPassed) {
      setAlert({
        type: 'error',
        message: 'Please complete DPA check in the "Security" tab'
      });
      return false;
    }

    return true;
  };

  const makeDPACheckRequired = useCallback(() => {
    sessionStorage.setItem(dpaCheckKey, 'true');
  }, [dpaCheckKey]);

  const maybeShowDPACheckConfirmationModal = ({ action, onOk }: { action: string; onOk?: () => void }) => {
    if (!hasCompletedDPACheck) {
      confirmationModal({
        title: 'Warning',
        content: `To ${action}, you must first complete the security (DPA) checks. Please click below to complete the check.`,
        okText: 'OK',
        onOk: () => onOk?.(),
        closable: true
      });
    } else {
      onOk?.();
    }
  };

  return {
    isDPACheckCR,
    hasCompletedDPACheck,
    dpaCheckHasPassed,
    validateDPACheck,
    makeDPACheckRequired,
    maybeShowDPACheckConfirmationModal
  };
};

export { useDPACheck };
